import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Briefcase, 
  UserCheck, 
  ClipboardList, 
  Calendar, 
  CheckCircle2, 
  Users,
  Clock,
  TrendingUp,
  Bookmark,
  Star,
  Target,
  BarChart3,
  FileText,
  ShieldAlert,
  ShieldCheck,
  ChevronDown
} from "lucide-react";
import { Card, CardContent } from "@/Components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/Components/ui/collapsible";
import { cn } from "@/lib/utils";
import useJob from "../../Hooks/jobHook";
import { getApiUrl } from "../../../config/api";
import {
  getCachedOverviewStats,
  isOverviewStatsCacheValid,
  setCachedOverviewStats,
} from "./overviewStatsCache";

// Hero stat card - Large prominent display for primary KPIs
function HeroStatCard({ value, label, icon: Icon, onClick, trend, trendLabel, accentColor = "primary" }) {
  const colorStyles = {
    primary: {
      bg: "bg-primary/5 dark:bg-primary/10",
      iconBg: "bg-primary/10 dark:bg-primary/20",
      iconText: "text-primary",
      value: "text-primary",
      hoverBorder: "hover:border-primary/30",
    },
    blue: {
      bg: "bg-blue-50/50 dark:bg-blue-950/20",
      iconBg: "bg-blue-100 dark:bg-blue-900/40",
      iconText: "text-blue-600 dark:text-blue-400",
      value: "text-blue-600 dark:text-blue-400",
      hoverBorder: "hover:border-blue-200 dark:hover:border-blue-800",
    },
    emerald: {
      bg: "bg-emerald-50/50 dark:bg-emerald-950/20",
      iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
      iconText: "text-emerald-600 dark:text-emerald-400",
      value: "text-emerald-600 dark:text-emerald-400",
      hoverBorder: "hover:border-emerald-200 dark:hover:border-emerald-800",
    },
    violet: {
      bg: "bg-violet-50/50 dark:bg-violet-950/20",
      iconBg: "bg-violet-100 dark:bg-violet-900/40",
      iconText: "text-violet-600 dark:text-violet-400",
      value: "text-violet-600 dark:text-violet-400",
      hoverBorder: "hover:border-violet-200 dark:hover:border-violet-800",
    },
  };

  const colors = colorStyles[accentColor] || colorStyles.primary;

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden",
        colors.bg,
        "border border-border/40 shadow-sm",
        "transition-all duration-200 ease-out",
        onClick && `cursor-pointer hover:shadow-md ${colors.hoverBorder} hover:-translate-y-0.5`
      )}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </span>
            <span className={cn("text-3xl font-bold tracking-tight", colors.value)}>
              {value}
            </span>
            {trendLabel && (
              <span className="text-xs text-muted-foreground/80 mt-1">
                {trendLabel}
              </span>
            )}
          </div>
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center",
            colors.iconBg, colors.iconText,
            "transition-all duration-200",
            onClick && "group-hover:scale-105"
          )}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Pipeline stat card - Smaller card for secondary KPIs
function PipelineStatCard({ value, label, icon: Icon, onClick, accentColor = "slate" }) {
  const colorStyles = {
    slate: {
      iconBg: "bg-slate-100 dark:bg-slate-800/50",
      iconText: "text-slate-500 dark:text-slate-400",
      hoverIconBg: "group-hover:bg-slate-200 dark:group-hover:bg-slate-700/50",
    },
    amber: {
      iconBg: "bg-amber-100/70 dark:bg-amber-900/30",
      iconText: "text-amber-600 dark:text-amber-400",
      hoverIconBg: "group-hover:bg-amber-100 dark:group-hover:bg-amber-900/50",
    },
    indigo: {
      iconBg: "bg-indigo-100/70 dark:bg-indigo-900/30",
      iconText: "text-indigo-600 dark:text-indigo-400",
      hoverIconBg: "group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/50",
    },
    teal: {
      iconBg: "bg-teal-100/70 dark:bg-teal-900/30",
      iconText: "text-teal-600 dark:text-teal-400",
      hoverIconBg: "group-hover:bg-teal-100 dark:group-hover:bg-teal-900/50",
    },
    rose: {
      iconBg: "bg-rose-100/70 dark:bg-rose-900/30",
      iconText: "text-rose-600 dark:text-rose-400",
      hoverIconBg: "group-hover:bg-rose-100 dark:group-hover:bg-rose-900/50",
    },
  };

  const colors = colorStyles[accentColor] || colorStyles.slate;

  return (
    <Card 
      className={cn(
        "group",
        "bg-card border border-border/30 shadow-sm",
        "transition-all duration-200 ease-out",
        onClick && "cursor-pointer hover:bg-muted/30 hover:border-border/50 hover:-translate-y-0.5"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
            colors.iconBg, colors.iconText,
            "transition-all duration-200",
            onClick && colors.hoverIconBg
          )}>
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-xl font-semibold text-foreground">
              {value}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {label}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact stat card - Smallest variant for collapsible section
function CompactStatCard({ value, label, icon: Icon, subtitle, variant = "default" }) {
  const variantStyles = {
    default: "bg-muted/30 border-border/30",
    screening: "bg-slate-50/50 dark:bg-slate-900/20 border-slate-200/40 dark:border-slate-700/40",
    personality: "bg-pink-50/50 dark:bg-pink-950/20 border-pink-200/40 dark:border-pink-800/40",
    behavioral: "bg-sky-50/50 dark:bg-sky-950/20 border-sky-200/40 dark:border-sky-800/40",
    coding: "bg-purple-50/50 dark:bg-purple-950/20 border-purple-200/40 dark:border-purple-800/40",
    warning: "bg-amber-50/50 dark:bg-amber-950/20 border-amber-200/40 dark:border-amber-800/40",
    success: "bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200/40 dark:border-emerald-800/40",
  };
  
  const iconStyles = {
    default: "bg-muted/50 text-muted-foreground",
    screening: "bg-slate-100/70 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400",
    personality: "bg-pink-100/70 dark:bg-pink-900/40 text-pink-500 dark:text-pink-400",
    behavioral: "bg-sky-100/70 dark:bg-sky-900/40 text-sky-500 dark:text-sky-400",
    coding: "bg-purple-100/70 dark:bg-purple-900/40 text-purple-500 dark:text-purple-400",
    warning: "bg-amber-100/70 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400",
    success: "bg-emerald-100/70 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400",
  };

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg border transition-colors duration-150",
      variantStyles[variant]
    )}>
      <div className={cn(
        "w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0",
        iconStyles[variant]
      )}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-lg font-semibold text-foreground">
          {value}
        </span>
        <span className="text-xs text-muted-foreground truncate">
          {label}
        </span>
        {subtitle && (
          <span className="text-[10px] text-muted-foreground/70">
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

// Section header component
function SectionHeader({ title, description }) {
  return (
    <div className="flex items-center gap-3">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        {title}
      </h3>
      <div className="flex-1 h-px bg-border/50" />
      {description && (
        <span className="text-xs text-muted-foreground/70">{description}</span>
      )}
    </div>
  );
}

// Inner component: receives jobs + jobsLoading so Overview can pass from single useJob() and avoid double fetch.
function StatsCardsInner({ jobs = [], jobsLoading = false, onRefreshJobs }) {
  const navigate = useNavigate();

  // Collapsible state with localStorage persistence
  const [isDetailsOpen, setIsDetailsOpen] = useState(() => {
    const saved = localStorage.getItem('overview_details_open');
    return saved ? JSON.parse(saved) : false;
  });
  
  useEffect(() => {
    localStorage.setItem('overview_details_open', JSON.stringify(isDetailsOpen));
  }, [isDetailsOpen]);
  
  const defaultStats = {
    openJobs: 0,
    pendingApplications: 0,
    lineupInterviews: 0,
    completedInterviews: 0,
    totalCompletedTests: 0,
    screeningCompleted: 0,
    behavioralCompleted: 0,
    codingCompleted: 0,
    personalityCompleted: 0,
    totalApplications: 0,
    shortlistedCount: 0,
    rejectedCount: 0,
    avgScore: 0,
  };
  const defaultHired = { totalHired: 0, recentHires: 0, totalApplications: 0 };
  const defaultIntegrity = { flaggedCandidates: 0, cleanCandidates: 0, avgPlagiarism: 0 };

  const cached = getCachedOverviewStats();
  const [stats, setStats] = useState(() => (cached?.stats ? { ...defaultStats, ...cached.stats } : defaultStats));
  const [hiredStats, setHiredStats] = useState(() => (cached?.hiredStats ? { ...defaultHired, ...cached.hiredStats } : defaultHired));
  const [integrityStats, setIntegrityStats] = useState(() => (cached?.integrityStats ? { ...cached.integrityStats } : defaultIntegrity));
  const [statsLoading, setStatsLoading] = useState(!isOverviewStatsCacheValid());

  // Single effect: prefer one /api/overview_stats call (<1s); fallback to 3 separate calls if needed
  useEffect(() => {
    const employerEmail = localStorage.getItem('employer_email');
    const companyId = localStorage.getItem('companyAccountId');
    if (!employerEmail) {
      setStatsLoading(false);
      return;
    }

    const params = new URLSearchParams({ company_email: employerEmail });
    if (companyId) params.set('company_id', companyId);
    const overviewUrl = getApiUrl(`/api/overview_stats?${params.toString()}`);

    const applyResult = (newStats, newHired, newIntegrity) => {
      if (newStats) setStats((prev) => ({ ...prev, ...newStats }));
      if (newHired) setHiredStats((prev) => ({ ...prev, ...newHired }));
      if (newIntegrity) setIntegrityStats(newIntegrity);
      setCachedOverviewStats({ stats: newStats, hiredStats: newHired, integrityStats: newIntegrity });
      setStatsLoading(false);
    };

    // 1) Try single aggregated endpoint first (one round-trip, target <1s)
    fetch(overviewUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.stats != null) {
          applyResult(data.stats, data.hiredStats ?? null, data.integrityStats ?? null);
          return;
        }
        throw new Error('Overview stats unavailable');
      })
      .catch(() => {
        // 2) Fallback: three parallel calls (legacy or when overview_stats not deployed)
        const fetchStats = () =>
          axios
            .get(getApiUrl(`/api/company_stats?company_email=${encodeURIComponent(employerEmail)}`))
            .then((res) => res.data)
            .catch((err) => {
              console.error('Error fetching stats:', err);
              return null;
            });

        const fetchHiredStats = () => {
          const url = getApiUrl(
            `/api/get_jobs_with_hired_count?company_email=${encodeURIComponent(employerEmail)}${companyId ? `&company_id=${companyId}` : ''}`
          );
          return fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (data?.success && data.jobs) {
                const totalHired = data.jobs.reduce((sum, job) => sum + (job.hired_count || 0), 0);
                const totalApps = data.jobs.reduce(
                  (sum, job) => sum + (job.total_applications || job.applications || 0),
                  0
                );
                return { totalHired, totalApplications: totalApps };
              }
              return null;
            })
            .catch((err) => {
              console.error('Error fetching hired stats:', err);
              return null;
            });
        };

        const fetchIntegrityStats = () => {
          if (!companyId) return Promise.resolve(null);
          const url = getApiUrl(
            `/api/candidatelist_without_jobID?company_email=${encodeURIComponent(employerEmail)}&companyId=${companyId}&page=1&per_page=500`
          );
          return fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              const candidates = Array.isArray(data) ? data : (data?.candidates || []);
              let flagged = 0,
                clean = 0,
                totalPlagiarism = 0,
                plagiarismCount = 0;
              candidates.forEach((candidate) => {
                const pct = parseFloat(candidate.plagiarism_percentage);
                if (!isNaN(pct)) {
                  totalPlagiarism += pct;
                  plagiarismCount++;
                  if (pct > 40) flagged++;
                  else clean++;
                }
              });
              return {
                flaggedCandidates: flagged,
                cleanCandidates: clean,
                avgPlagiarism: plagiarismCount > 0 ? (totalPlagiarism / plagiarismCount).toFixed(1) : 0,
              };
            })
            .catch((err) => {
              console.error('Error fetching integrity stats:', err);
              return null;
            });
        };

        Promise.allSettled([fetchStats(), fetchHiredStats(), fetchIntegrityStats()]).then(
          ([statsResult, hiredResult, integrityResult]) => {
            const newStats = statsResult.status === 'fulfilled' && statsResult.value ? statsResult.value : null;
            const newHired = hiredResult.status === 'fulfilled' && hiredResult.value ? hiredResult.value : null;
            const newIntegrity =
              integrityResult.status === 'fulfilled' && integrityResult.value ? integrityResult.value : null;
            applyResult(newStats, newHired, newIntegrity);
          }
        );
      });
  }, []);

  const loading = jobsLoading || statsLoading;
  const totalApps = hiredStats.totalApplications || stats.totalApplications || 0;
  // Open Jobs & Hire Rate denominator from same single API (stats.openJobs); fallback to job list when using legacy 3-call
  const totalJobs = typeof stats.openJobs === "number" ? stats.openJobs : (jobs?.length ?? 0);
  const conversionRate =
    totalJobs > 0 ? ((hiredStats.totalHired / totalJobs) * 100).toFixed(1) : "0.0";

  // Instant shell: always show layout; use placeholders when loading so page feels fast
  const openJobsVal = loading ? "—" : (totalJobs ?? 0);
  const totalAppsVal = loading ? "—" : (totalApps ?? 0);
  const totalHiredVal = loading ? "—" : (hiredStats.totalHired ?? 0);
  const rateVal = loading ? "—" : `${conversionRate}%`;

  const safeValue = (val) => {
    if (loading) return "—";
    return val ?? 0;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <HeroStatCard
          value={openJobsVal}
          label="Open Jobs"
          icon={Briefcase}
          onClick={() => navigate('/hirewithus/my-jobs')}
          accentColor="blue"
        />
        
        <HeroStatCard
          value={totalAppsVal}
          label="Total Applications"
          icon={FileText}
          onClick={() => navigate('/hirewithus/interview')}
          accentColor="violet"
        />
        <HeroStatCard
          value={totalHiredVal}
          label="Total Hired"
          icon={CheckCircle2}
          onClick={() => navigate('/hirewithus/hired-profiles')}
          accentColor="emerald"
        />
        <HeroStatCard
          value={rateVal}
          label="Hire Rate"
          icon={Target}
          trendLabel="Applications → Hired"
          accentColor="primary"
        />
      </div>

      {/* Pipeline Section - Secondary KPIs */}
      <div className="flex flex-col gap-3">
        <SectionHeader title="Pipeline" />
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <PipelineStatCard
            value={loading ? "—": (stats.pendingApplications || 0)}
            label="Pending Review"
            icon={ClipboardList}
            accentColor="amber"
          />
          <PipelineStatCard
            value={loading ? "—": (stats.lineupInterviews || 0)}
            label="Scheduled Interviews"
            icon={Calendar}
            accentColor="indigo"
          />
          <PipelineStatCard
            value={loading ? "—": (stats.totalCompletedTests || 0)}
            label="Tests Completed"
            icon={UserCheck}
            accentColor="teal"
          />
          <PipelineStatCard
            value={loading ? "—": (stats.shortlistedCount || 0)}
            label="Shortlisted Candidates"
            icon={Bookmark}
            accentColor="rose"
          />
        </div>
      </div>

      {/* Collapsible Assessment & Integrity Section */}
      <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CollapsibleTrigger className="flex items-center gap-3 w-full group">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Assessment & Integrity Breakdown
          </h3>
          <div className="flex-1 h-px bg-border/50" />
          <ChevronDown 
            className={cn(
              "w-4 h-4 text-muted-foreground transition-transform duration-200",
              isDetailsOpen && "rotate-180"
            )} 
          />
        </CollapsibleTrigger>
        
        <CollapsibleContent className="pt-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <CompactStatCard
              value={loading ? "—": (stats.screeningCompleted || 0)}
              label="Screening"
              icon={Users}
              variant="screening"
            />
            
            <CompactStatCard
              value={loading ? "—": (stats.personalityCompleted || 0)}
              label="Personality"
              icon={Star}
              variant="personality"
            />
            
            <CompactStatCard
              value={loading ? "—": (stats.behavioralCompleted || 0)}
              label="Behavioral"
              icon={Clock}
              variant="behavioral"
            />
            
            <CompactStatCard
              value={loading ? "—": (stats.codingCompleted || 0)}
              label="Coding"
              icon={TrendingUp}
              variant="coding"
            />
            
            <CompactStatCard
              value={loading ? "—": integrityStats.flaggedCandidates}
              label="Flagged"
              icon={ShieldAlert}
              subtitle=">40% plagiarism"
              variant="warning"
            />
            
            <CompactStatCard
              value={loading ? "—": integrityStats.cleanCandidates}
              label="Clean"
              icon={ShieldCheck}
              subtitle="≤40% plagiarism"
              variant="success"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// Wrapper: when jobs/jobsLoading passed (Overview), no useJob — single get_jd for whole Overview.
function StatsCardsWithJobHook() {
  const { jobs, loading } = useJob();
  return <StatsCardsInner jobs={jobs} jobsLoading={loading} />;
}

function StatsCards(props) {
  if (props.jobs !== undefined || props.jobsLoading !== undefined) {
    return (
      <StatsCardsInner
        jobs={props.jobs ?? []}
        jobsLoading={props.jobsLoading ?? false}
        onRefreshJobs={props.onRefreshJobs}
      />
    );
  }
  return <StatsCardsWithJobHook />;
}

export default StatsCards;